import React from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { AiFillEdit } from "react-icons/ai";
import { Editor } from '@tinymce/tinymce-react';

const HiringFormFieldRenderer = ({
  field,
  groupIndex,
  fieldIndex,
  fieldValues,
  editingField,
  editLabelValue,
  editOptionsValue,
  handleChange,
  handleDeleteField,
  startEditing,
  saveEdit,
  setEditLabelValue,
  setEditOptionsValue,
  setEditingField
}) => {
  const fieldId = field?.id ?? `${groupIndex}-${fieldIndex}`;
  const value = fieldValues[fieldId] ?? '';
  const { type, label, options, placeholder, mandatory } = field;

  const labelStyle = 'text-sm whitespace mr-[5px]';
  const inputStyle = 'flex-1 border px-3 py-2 bg-gray-200';

  return (
    <div className="relative">
      <div className="absolute -top-2 -right-1 flex gap-1 z-50">
        <button
          onClick={() => handleDeleteField(groupIndex, fieldIndex)}
          className="bg-red-600 hover:bg-red-800 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full mx-auto mt-2"
          title="Delete field"
        >
          x
        </button>
      </div>

      {(() => {
        switch (type) {
          case "heading": {
            const fieldValue = fieldValues?.[fieldId] ?? {
              heading: "",
              subheading: "",
            };

            const headingText = fieldValue.heading || "";
            const subheadingText = fieldValue.subheading || "";

            const isEditing =
              (editingField?.groupIndex === groupIndex &&
                editingField?.fieldIndex === fieldIndex &&
                editingField?.type === "heading") ||
              (!headingText && !subheadingText);

            return (
              <div
                key={fieldId}
                className="mb-4 p-4 bg-[#E1E1E1] rounded text-center text-gray-700"
              >
                {isEditing ? (
                  <div className="flex flex-col gap-2">
                    <input
                      type="text"
                      placeholder="Heading"
                      value={
                        editLabelValue?.heading !== undefined
                          ? editLabelValue.heading
                          : headingText
                      }
                      onChange={(e) =>
                        setEditLabelValue((prev) => ({
                          ...(prev || {}),
                          heading: e.target.value,
                        }))
                      }
                      className="border p-1"
                    />
                    <input
                      type="text"
                      placeholder="Subheading"
                      value={
                        editLabelValue?.subheading !== undefined
                          ? editLabelValue.subheading
                          : subheadingText
                      }
                      onChange={(e) =>
                        setEditLabelValue((prev) => ({
                          ...(prev || {}),
                          subheading: e.target.value,
                        }))
                      }
                      className="border p-1"
                    />
                    <div className="flex gap-2 justify-center">
                      <button
                        onClick={() => {
                          const updatedValue = {
                            heading: editLabelValue?.heading || "",
                            subheading: editLabelValue?.subheading || "",
                          };
                          handleChange(
                            { target: { value: updatedValue } },
                            fieldId,
                            "heading"
                          );
                          setEditingField(null);
                        }}
                        className="bg-blue-500 text-white px-2 py-1 rounded"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingField(null)}
                        className="bg-gray-500 text-white px-2 py-1 rounded"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => {
                      startEditing(groupIndex, fieldIndex, field, "heading");
                      setEditLabelValue({
                        heading: headingText,
                        subheading: subheadingText,
                      });
                    }}
                  >
                    <h2 className="text-lg font-semibold mb-2">
                      {headingText}
                    </h2>
                    {subheadingText && (
                      <p className="text-lg">{subheadingText}</p>
                    )}
                  </div>
                )}
              </div>
            );
          }
          case "select":
            return (
              <div
                className={`flex items-start gap-2 ${
                  field.styles?.optionsContainer || ""
                }`}
                key={fieldId}
              >
                {editingField?.groupIndex === groupIndex &&
                editingField?.fieldIndex === fieldIndex &&
                editingField?.type === "label" ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editLabelValue}
                      onChange={(e) => setEditLabelValue(e.target.value)}
                      className="border p-1 w-40"
                    />
                    <button
                      onClick={saveEdit}
                      className="bg-blue-500 text-white px-2 py-1 rounded text-xs"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingField(null)}
                      className="bg-gray-500 text-white px-2 py-1 rounded text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <label
                    className={`text-sm ${labelStyle} cursor-pointer`}
                    onClick={() =>
                      startEditing(groupIndex, fieldIndex, field, "label")
                    }
                  >
                    {label}{" "}
                    {mandatory && <span className="text-red-500 ml-1">*</span>}
                  </label>
                )}
                <div className="flex flex-row gap-2">
                  <select
                    className={`${inputStyle} min-w-max max-w-[150px]`}
                    value={value}
                    onChange={(e) => handleChange(e, fieldId, type)}
                  >
                    <option value="">Select</option>
                    {options?.map((opt, idx) => (
                      <option key={idx} value={opt} className="">
                        {opt}
                      </option>
                    ))}
                  </select>
                  {editingField?.groupIndex === groupIndex &&
                  editingField?.fieldIndex === fieldIndex &&
                  editingField?.type === "options" ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={editOptionsValue}
                        onChange={(e) => setEditOptionsValue(e.target.value)}
                        className="border p-1 flex-1"
                        placeholder="Enter options separated by commas"
                      />
                      <button
                        onClick={saveEdit}
                        className="bg-blue-500 text-white px-2 py-1 rounded text-xs"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingField(null)}
                        className="bg-gray-500 text-white px-2 py-1 rounded text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() =>
                        startEditing(groupIndex, fieldIndex, field, "options")
                      }
                      className="text-xs text-blue-500 underline"
                    >
                      <AiFillEdit className="cursor-pointer w-4 h-4 text-slate-600" />
                    </button>
                  )}
                </div>
              </div>
            );
          case "input":
          case "date":
            return (
              <div
                className={`flex items-start gap-2 ${
                  field.styles?.optionsContainer || ""
                }`}
                key={fieldId}
              >
                {editingField?.groupIndex === groupIndex &&
                editingField?.fieldIndex === fieldIndex &&
                editingField?.type === "label" ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editLabelValue}
                      onChange={(e) => setEditLabelValue(e.target.value)}
                      className="border p-1 w-40"
                    />
                    <button
                      onClick={saveEdit}
                      className="bg-blue-500 text-white px-2 py-1 rounded text-xs"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingField(null)}
                      className="bg-gray-500 text-white px-2 py-1 rounded text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <label
                    className={`text-sm ${labelStyle} cursor-pointer`}
                    onClick={() =>
                      startEditing(groupIndex, fieldIndex, field, "label")
                    }
                  >
                    {label}{" "}
                    {mandatory && <span className="text-red-500 ml-1">*</span>}
                  </label>
                )}
                {type === "input" && (
                  <input
                    type="text"
                    className={inputStyle}
                    value={value}
                    onChange={(e) => handleChange(e, fieldId, type)}
                    placeholder={placeholder}
                  />
                )}
                {type === "date" && (
                  <input
                    type="date"
                    className={inputStyle}
                    value={value}
                    onChange={(e) => handleChange(e, fieldId, type)}
                  />
                )}
              </div>
            );
          case "textarea":
            return (
              <div className="mb-4" key={fieldId} style={{ clear: "both" }}>
                {editingField?.groupIndex === groupIndex &&
                editingField?.fieldIndex === fieldIndex &&
                editingField?.type === "label" ? (
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={editLabelValue}
                      onChange={(e) => setEditLabelValue(e.target.value)}
                      className="border p-1 w-40"
                    />
                    <button
                      onClick={saveEdit}
                      className="bg-blue-500 text-white px-2 py-1 rounded text-xs"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingField(null)}
                      className="bg-gray-500 text-white px-2 py-1 rounded text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <label
                    className="block mb-1 cursor-pointer"
                    onClick={() =>
                      startEditing(groupIndex, fieldIndex, field, "label")
                    }
                  >
                    {label}{" "}
                    {mandatory && <span className="text-red-500 ml-1">*</span>}
                  </label>
                )}
                <textarea
                  className="w-full border rounded p-2 resize-none overflow-hidden"
                  value={value}
                  ref={(el) => {
                    if (el) {
                      el.style.height = "auto";
                      el.style.height = `${el.scrollHeight}px`;
                    }
                  }}
                  onInput={(e) => {
                    e.target.style.height = "auto";
                    e.target.style.height = `${e.target.scrollHeight}px`;
                  }}
                  onChange={(e) => handleChange(e, fieldId, type)}
                  placeholder={placeholder}
                />
              </div>
            );
          case "textEditor": {
            return (
              <div className="mb-4" key={fieldId} style={{ clear: "both" }}>
                <Editor
                  apiKey={process.env.REACT_APP_TINY_MCE_KEY} // Optional, can leave blank for local use
                  onEditorChange={(content) => {
                    handleChange({ target: { value: content } }, fieldId, type);
                  }}
                  value={value}
                  init={{
                    menubar: "file edit view insert format tools table help",
                    plugins: ["lists",
                      "advlist autolink link image charmap preview anchor",
                      "searchreplace visualblocks code fullscreen",
                      "insertdatetime media table paste help wordcount",
                      "autoresize",
                      "table",
                    ],
                    toolbar:
                      "undo redo | formatselect | " +
                      "bold italic underline strikethrough blockquote | " +
                      "alignleft aligncenter alignright alignjustify | " +
                      "outdent indent | " +
                      "forecolor backcolor | fontselect fontsizeselect | " +
                      "link image media | table tabledelete | removeformat | help" + " bullist numlist",
                    autoresize_bottom_margin: 10, // Optional: spacing below the editor
                    autoresize_min_height: 150, // Optional: minimum height
                    autoresize_overflow_padding: 5, // Optional: smooth resize
                    content_style:
                      "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                  }}
                />
              </div>
            );
          }
          case "radio":
          case "checkbox":
            return (
              <div
                className={`flex items-start gap-2 ${
                  field.styles?.optionsContainer || ""
                }`}
                key={fieldId}
              >
                {editingField?.groupIndex === groupIndex &&
                editingField?.fieldIndex === fieldIndex &&
                editingField?.type === "label" ? (
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={editLabelValue}
                      onChange={(e) => setEditLabelValue(e.target.value)}
                      className="border p-1 w-40"
                    />
                    <button
                      onClick={saveEdit}
                      className="bg-blue-500 text-white px-2 py-1 rounded text-xs"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingField(null)}
                      className="bg-gray-500 text-white px-2 py-1 rounded text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <label
                    className="block mb-1 cursor-pointer"
                    onClick={() =>
                      startEditing(groupIndex, fieldIndex, field, "label")
                    }
                  >
                    {label}{" "}
                    {mandatory && <span className="text-red-500 ml-1">*</span>}
                  </label>
                )}

                {editingField?.groupIndex === groupIndex &&
                editingField?.fieldIndex === fieldIndex &&
                editingField?.type === "options" ? (
                  <div className="flex flex-col gap-2 mb-2">
                    <input
                      type="text"
                      value={editOptionsValue}
                      onChange={(e) => setEditOptionsValue(e.target.value)}
                      className="border p-1"
                      placeholder="Enter options separated by commas"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={saveEdit}
                        className="bg-blue-500 text-white px-2 py-1 rounded text-xs"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingField(null)}
                        className="bg-gray-500 text-white px-2 py-1 rounded text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-row gap-4">
                  <div className="flex flex-row gap-4 flex-wrap">
                    {options?.map((opt, idx) => (
                      <label key={idx} className="flex items-center gap-2">
                        <input
                          type={type}
                          name={fieldId}
                          value={opt}
                          checked={
                            type === "checkbox"
                              ? (value || []).includes(opt)
                              : value === opt
                          }
                          onChange={(e) => handleChange(e, fieldId, type)}
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                    <button
                      onClick={() =>
                        startEditing(groupIndex, fieldIndex, field, "options")
                      }
                      className="text-xs text-blue-500 underline"
                    >
                      <AiFillEdit className="cursor-pointer w-4 h-4 text-slate-600" />
                    </button>
                  </div>
                )}

                
              </div>
            );
          case "file":
            return (
              <div key={fieldId} className="mb-4">
                {editingField?.groupIndex === groupIndex &&
                editingField?.fieldIndex === fieldIndex &&
                editingField?.type === "label" ? (
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={editLabelValue}
                      onChange={(e) => setEditLabelValue(e.target.value)}
                      className="border p-1 w-40"
                    />
                    <button
                      onClick={saveEdit}
                      className="bg-blue-500 text-white px-2 py-1 rounded text-xs"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingField(null)}
                      className="bg-gray-500 text-white px-2 py-1 rounded text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <label
                    className={`${labelStyle} cursor-pointer`}
                    onClick={() =>
                      startEditing(groupIndex, fieldIndex, field, "label")
                    }
                  >
                    {label}
                  </label>
                )}
                <input
                  type="file"
                  onChange={(e) => handleChange(e, fieldId, type)}
                  className="block mb-2"
                  accept="image/*"
                />
                {value &&
                  (typeof value === "string" ? (
                    <div className="mt-2">
                      <img
                        src={value}
                        alt="Uploaded preview"
                        className="max-w-full h-auto max-h-40 rounded border"
                      />
                    </div>
                  ) : value instanceof File ? (
                    <div className="mt-2">
                      <img
                        src={URL.createObjectURL(value)}
                        alt="Selected preview"
                        className="max-w-full h-auto max-h-40 rounded border"
                      />
                    </div>
                  ) : null)}
              </div>
            );
          case "text":
            return (
              <div>
                {editingField?.groupIndex === groupIndex &&
                editingField?.fieldIndex === fieldIndex &&
                editingField?.type === "label" ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editLabelValue}
                      onChange={(e) => setEditLabelValue(e.target.value)}
                      className="border p-1 w-40"
                    />
                    <button
                      onClick={saveEdit}
                      className="bg-blue-500 text-white px-2 py-1 rounded text-xs"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingField(null)}
                      className="bg-gray-500 text-white px-2 py-1 rounded text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <label
                    className={`text-sm ${labelStyle} cursor-pointer`}
                    onClick={() =>
                      startEditing(groupIndex, fieldIndex, field, "label")
                    }
                  >
                    {label}{" "}
                    {mandatory && <span className="text-red-500 ml-1">*</span>}
                  </label>
                )}
              </div>
            );
          default:
            return <div key={fieldId}>Unsupported field type: {type}</div>;
        }
      })()}
    </div>
  );
};

export default HiringFormFieldRenderer;